import React, { FC, useRef, useState, useMemo, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import { Flex, Spin } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { selectCurrentZone } from '../../store/global'
import NavigationHeader from 'components/NavigationHeader/NavigationHeader'
import STROverviewBar from 'components/STROverviewBar/STROverviewBar'
import { fetchUiConfig } from 'services/strAdminApi'
import { BaseTableOverview, CurrentZone } from 'utils/types'
import { STREntityForTable } from 'containers/STRTablePage/types'
import { useVerticalOverviewQuery } from 'hooks/useVerticalOverviewQuery'
import { useVerticalEntitiesQuery } from 'hooks/useVerticalEntitiesQuery'
import useVerticalSearchAndSort from 'hooks/useVerticalSearchAndSort'
import { useSelector } from 'react-redux'
import { useEmptyColumnsQuery } from 'hooks/useEmptyColumnsQuery'
import VerticalTable from 'UI/VerticalTable/VerticalTable'
import ExportCSVTableVertical from 'components/ExportCSVTableVertical/ExportCSVTableVertical'
import Search from 'UI/Search/Search'
import { keysForEmptyColumnsRequest, strColumns, estimatedRevenueColumn } from './utils'
import { COLUMNS_IDS } from 'UI/VerticalTable/utils'
import { PAGE_SIZE } from 'components/verticals/constants'
import NoDataContainer from 'components/NoDataContainer'
import styles from './STRTablePage.scss'

const STRTablePage: FC = () => {
  const history = useHistory()
  const currentZone: CurrentZone = useSelector(selectCurrentZone)
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const isFetchingRef = useRef<boolean>(false)
  const [page, setPage] = useState<number>(1)

  const { searchValue, searchValueDebounced, sorting, setSearchValue, setSorting } = useVerticalSearchAndSort([
    { id: 'totals.totalListings', desc: false },
  ])

  const { data: uiConfigData } = useQuery({
    queryKey: ['str-ui-config'],
    queryFn: fetchUiConfig,
    staleTime: 5 * 60 * 1000,
  })

  const { data: emptyColumnsData, isFetched: emptyColumnsIsFetched } = useEmptyColumnsQuery({
    verticalIndices: currentZone?.strIndices,
    currentQueryKey: 'str-entities-key',
    keys: keysForEmptyColumnsRequest,
  })

  const { data: overviewData, isLoading: isLoadingOverview } = useVerticalOverviewQuery<BaseTableOverview>({
    verticalIndices: currentZone?.strIndices,
    currentQueryKey: 'str-overview-key',
    searchValue: searchValueDebounced,
  })

  const { verticalEntities, fetchNextPage, isFetching, isLoading, isError } =
    useVerticalEntitiesQuery<STREntityForTable>({
      verticalIndices: currentZone?.strIndices,
      currentQueryKey: 'str-entities-key',
      searchValue: searchValueDebounced,
      sorting,
      pageSize: PAGE_SIZE,
    })

  const revenueEnabled = !!(overviewData as any)?.revenueEnabled

  const visibleColumns = useMemo(() => {
    if (revenueEnabled) return strColumns
    return strColumns.filter(c => c !== (estimatedRevenueColumn as any))
  }, [revenueEnabled])

  const hasNoData = useMemo(() => {
    return !isLoading && !isError && (!verticalEntities || verticalEntities.length === 0) && !searchValueDebounced
  }, [isLoading, isError, verticalEntities, searchValueDebounced])

  // Reset fetching ref when loading completes
  React.useEffect(() => {
    if (!isFetching) {
      isFetchingRef.current = false
    }
  }, [isFetching])

  // Handle infinite scroll via the window scroll event (page scrolls via browser, not inner table)
  React.useEffect(() => {
    const handleWindowScroll = () => {
      if (!fetchNextPage || !overviewData?.total) return
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement
      if (
        scrollTop + clientHeight + 10 >= scrollHeight &&
        !isFetching &&
        !isFetchingRef.current &&
        page * PAGE_SIZE < overviewData.total
      ) {
        isFetchingRef.current = true
        setPage(prev => {
          fetchNextPage()
          return prev + 1
        })
      }
    }
    window.addEventListener('scroll', handleWindowScroll)
    return () => window.removeEventListener('scroll', handleWindowScroll)
  }, [fetchNextPage, isFetching, overviewData?.total, page])

  return (
    <div className={styles.page}>
      <div>
        <NavigationHeader />
        <h1 className={styles.title}>SHORT TERM RENTAL</h1>
      </div>
      {hasNoData ? (
        <NoDataContainer verticalName='Short Term Rentals' zoneName={currentZone?.name} />
      ) : (
        <>
          <section className={styles.overviewSection}>
            <STROverviewBar
              isLoading={isLoadingOverview}
              hostsCount={overviewData?.summary?.entitiesCount?.total || 0}
              listingsCount={(overviewData?.summary as any)?.listingsCount || 0}
              documentedRevenue={overviewData?.summary?.incomeCounts?.documented || 0}
              reportedRevenue={overviewData?.summary?.incomeCounts?.reported || 0}
              taxGap={overviewData?.summary?.incomeCounts?.untaxed || 0}
              overviewBarConfig={uiConfigData?.overviewBar}
              revenueEnabled={(overviewData as any)?.revenueEnabled}
              revenueRange={(overviewData as any)?.revenueRange}
            />
          </section>
          {isLoading ? (
            <Flex align='center' gap='middle' justify='center' style={{ padding: '45px 14px' }}>
              <Spin size='large' />
            </Flex>
          ) : isError ? (
            <Alert severity='error' sx={{ fontFamily: 'Source Sans Pro', fontSize: '1rem', alignItems: 'center' }}>
              Something went wrong
            </Alert>
          ) : (
            <div className={styles.tableSection}>
              {emptyColumnsIsFetched && (
                <VerticalTable
                  pageSize={PAGE_SIZE}
                  enableColumnPicker
                  tableId='str'
                  dataSource={verticalEntities as any[]}
                  columns={visibleColumns}
                  total={overviewData?.total || 0}
                  loading={isFetching}
                  disabledColumns={emptyColumnsData}
                  onSelectionChange={setSelectedIds}
                  removeFromPickerColumns={[COLUMNS_IDS.hostName, COLUMNS_IDS.ecommerceDocumentedRevenue]}
                  rowClassName={styles.tableRow}
                  scroll={{ x: '100%', y: undefined }}
                  onRow={record => {
                    return {
                      onClick: () => {
                        window.open(`${window.location.origin}/vertical/short-term-rentals/${record.id}`, '_blank')
                      },
                    }
                  }}
                  onChange={(pagination, filters, sorter) => {
                    if (!Array.isArray(sorter)) {
                      if (sorter.order) {
                        setSorting([{ id: sorter.columnKey as string, desc: sorter.order === 'descend' }])
                      } else {
                        setSorting([])
                      }
                    }
                  }}
                >
                  <div className={styles.counter}>
                    {`Short Term Rentals: `}
                    <span>{selectedIds.length > 0 ? `${selectedIds.length} selected` : overviewData?.total}</span>
                  </div>
                  <Search
                    placeholder='input search text'
                    enterButton
                    allowClear
                    style={{ width: 264 }}
                    loading={isFetching}
                    onSearch={newSearchValue => setSearchValue(newSearchValue)}
                  />
                  <ExportCSVTableVertical
                    zoneIndex={currentZone?.strIndices}
                    csvFileName='Short-Term-Rentals'
                    searchValue={searchValue}
                    selectedIds={selectedIds}
                    sorting={sorting}
                    total={overviewData?.total}
                  />
                  {uiConfigData?.uiConfigEnabled && (
                    <Button
                      variant='outlined'
                      size='small'
                      onClick={() => history.push('/str-admin')}
                      sx={{ ml: 1, fontFamily: 'Source Sans Pro', textTransform: 'none' }}
                    >
                      Manage Hosts
                    </Button>
                  )}
                </VerticalTable>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default STRTablePage
